import { useCallback, useEffect, useState } from "react";
import i18next from "i18next";

import { api } from "../api/client";

export interface DropdownOptionItem {
  id: number;
  code: string;
  label: string;
  label_es: string | null;
  region_id: number | null;
  sort_order: number;
  is_default: boolean;
  is_active: boolean;
}

export interface DropdownListItem {
  id: number;
  code: string;
  name: string;
  category: string;
  description: string | null;
  is_system: boolean;
  is_active: boolean;
  options: DropdownOptionItem[];
}

let cachedLists: DropdownListItem[] | null = null;
let loadPromise: Promise<DropdownListItem[]> | null = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

async function fetchAll(): Promise<DropdownListItem[]> {
  if (cachedLists) return cachedLists;
  if (loadPromise) return loadPromise;
  loadPromise = api
    .get<DropdownListItem[]>("/api/dropdowns")
    .then((data) => {
      cachedLists = data;
      notifyListeners();
      return data;
    })
    .catch(() => {
      loadPromise = null;
      return [];
    });
  return loadPromise;
}

export function invalidateDropdownCache(): void {
  cachedLists = null;
  loadPromise = null;
  void fetchAll();
}

export function useDropdownLists(): {
  lists: DropdownListItem[];
  loading: boolean;
  refresh: () => void;
} {
  const [lists, setLists] = useState<DropdownListItem[]>(cachedLists ?? []);
  const [loading, setLoading] = useState(!cachedLists);

  useEffect(() => {
    const update = () => {
      if (cachedLists) {
        setLists(cachedLists);
        setLoading(false);
      }
    };
    listeners.add(update);
    void fetchAll().then(() => update());
    return () => {
      listeners.delete(update);
    };
  }, []);

  const refresh = useCallback(() => {
    invalidateDropdownCache();
  }, []);

  return { lists, loading, refresh };
}

export function useDropdownOptions(
  listCode: string,
  regionId?: number | null,
): { value: string; label: string }[] {
  const { lists } = useDropdownLists();
  const lang = i18next.language || "en-GB";
  const isEs = lang === "es";

  const list = lists.find((l) => l.code === listCode);
  if (!list) return [];

  let options = list.options.filter((o) => o.is_active);
  if (regionId != null) {
    options = options.filter(
      (o) => o.region_id === null || o.region_id === regionId,
    );
  }

  return options.map((o) => ({
    value: o.code,
    label: isEs && o.label_es ? o.label_es : o.label,
  }));
}
